
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import {
  FaTimes, FaChartBar, FaFileCsv, FaFileExcel, FaSpinner, FaUser,
} from 'react-icons/fa';
import './ModalConsolidadoPagadores.css';

const fmtCurrency = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v ?? 0);

const monthRange = () => {
  const d = new Date();
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0');
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  return { start: `${y}-${m}-01`, end: `${y}-${m}-${last}` };
};

export default function ModalConsolidadoPagadores({ isOpen, onClose }) {
  const range = monthRange();
  const [dataInicio, setDataInicio] = useState(range.start);
  const [dataFim,    setDataFim]    = useState(range.end);
  const [loading,    setLoading]    = useState(false);
  const [exporting,  setExporting]  = useState(null);
  const [dados,      setDados]      = useState({ pagadores: [], total_geral: 0, qtd_geral: 0 });

  const fetchConsolidado = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dataInicio) params.dataInicio = dataInicio;
      if (dataFim)    params.dataFim    = dataFim;
      const { data } = await api.get('/api/financeiro/pagadores/consolidado', { params });
      setDados({
        pagadores:   Array.isArray(data?.pagadores) ? data.pagadores : [],
        total_geral: data?.total_geral || 0,
        qtd_geral:   data?.qtd_geral   || 0,
      });
    } catch {
      toast.error('Erro ao carregar consolidado.');
      setDados({ pagadores: [], total_geral: 0, qtd_geral: 0 });
    } finally {
      setLoading(false);
    }
  }, [dataInicio, dataFim]);

  useEffect(() => { if (isOpen) fetchConsolidado(); }, [isOpen, fetchConsolidado]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const exportar = async (formato) => {
    setExporting(formato);
    try {
      const params = { formato };
      if (dataInicio) params.dataInicio = dataInicio;
      if (dataFim)    params.dataFim    = dataFim;
      const res = await api.get('/api/financeiro/pagadores/consolidado/exportar', {
        params,
        responseType: 'blob',
      });
      const blob = new Blob([res.data]);
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement('a');
      const sufixo = [dataInicio, dataFim].filter(Boolean).join('_') || 'completo';
      a.href = url;
      a.download = `consolidado-pagadores-${sufixo}.${formato === 'csv' ? 'csv' : 'xlsx'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Erro ao exportar arquivo.');
    } finally {
      setExporting(null);
    }
  };

  if (!isOpen) return null;

  const temDados = dados.pagadores.length > 0;

  return (
    <div className="mcons-overlay" onMouseDown={onClose}>
      <div
        className="mcons-modal"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Consolidado por pagador"
      >
        {/* Header */}
        <div className="mcons-header">
          <div className="mcons-header-title">
            <span className="mcons-header-icon"><FaChartBar /></span>
            <div>
              <h2>Consolidado por pagador</h2>
              <p>Total pago agrupado por pagador</p>
            </div>
          </div>
          <button className="mcons-close" onClick={onClose} aria-label="Fechar"><FaTimes /></button>
        </div>

        {/* Toolbar: datas + export */}
        <div className="mcons-toolbar">
          <div className="mcons-dates">
            <div className="mcons-date-field">
              <label>De</label>
              <input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
            </div>
            <div className="mcons-date-field">
              <label>Até</label>
              <input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
            </div>
            <button
              className="mcons-clear"
              onClick={() => { setDataInicio(''); setDataFim(''); }}
              title="Ver todo o histórico"
            >
              Tudo
            </button>
          </div>
          <div className="mcons-exports">
            <button className="mcons-exp csv" onClick={() => exportar('csv')} disabled={!temDados || !!exporting}>
              {exporting === 'csv' ? <FaSpinner className="mcons-spin" /> : <FaFileCsv />} CSV
            </button>
            <button className="mcons-exp xlsx" onClick={() => exportar('xlsx')} disabled={!temDados || !!exporting}>
              {exporting === 'xlsx' ? <FaSpinner className="mcons-spin" /> : <FaFileExcel />} Excel
            </button>
          </div>
        </div>

        {/* Tabela */}
        <div className="mcons-body">
          {loading ? (
            <div className="mcons-state"><FaSpinner className="mcons-spin" /> Carregando…</div>
          ) : !temDados ? (
            <div className="mcons-state">
              <FaUser className="mcons-empty-icon" />
              Nenhum pagamento com pagador no período selecionado.
            </div>
          ) : (
            <table className="mcons-table">
              <thead>
                <tr>
                  <th>Pagador</th>
                  <th>Documento</th>
                  <th className="num">Lançamentos</th>
                  <th className="num">Total pago</th>
                </tr>
              </thead>
              <tbody>
                {dados.pagadores.map((p) => (
                  <tr key={p.pagador_id}>
                    <td>
                      <div className="mcons-pagador-cell">
                        <span className="mcons-avatar">{p.pagador_nome.charAt(0).toUpperCase()}</span>
                        <span className="mcons-nome">
                          {p.pagador_nome}
                          {!p.pagador_ativo && <span className="mcons-inativo">inativo</span>}
                        </span>
                      </div>
                    </td>
                    <td className="mcons-doc">{p.pagador_documento || '—'}</td>
                    <td className="num">{p.qtd_lancamentos}</td>
                    <td className="num mcons-total-cell">{fmtCurrency(p.total_pago)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={2}>TOTAL GERAL</td>
                  <td className="num">{dados.qtd_geral}</td>
                  <td className="num">{fmtCurrency(dados.total_geral)}</td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}